import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
// html2canvas-pro and jspdf are dynamically imported inside buildPdfBlob()
// so they don't inflate the app's initial bundle.
import { takeUntil } from 'rxjs/operators';
import { UserTypeWithOrders } from '../../../../../../../../shared/schemas/userSchema';
import { addUserToStore } from '../../../../../store/slices/usersSlice/usersSlice.actions';
import { selectAllUsers } from '../../../../../store/slices/usersSlice/usersSlice.selector';
import { AppStore } from '../../../../../store/store';
import { UsersService } from '../../../services/users.service';
import { HebcalService, ShabbatData } from './services/getHebrewDateService';

@Component({
  selector: 'app-print-balance-table',
  templateUrl: './print-balance-table.component.html',
  styleUrls: ['./print-balance-table.component.scss'],
})
export class PrintBalanceTableComponent implements OnInit, OnDestroy {
  usersList$: Observable<UserTypeWithOrders[]> = this.store.pipe(select(selectAllUsers));
  usersPart1: UserTypeWithOrders[] = [];
  usersPart2: UserTypeWithOrders[] = [];
  shabbatData: ShabbatData | null = null;

  pdfBusy = false;

  /**
   * True only if the browser truly supports sharing a File via the Web
   * Share API (mobile Safari / Android Chrome). Desktop typically →
   * false; the WhatsApp button hides in that case and admins download
   * the PDF and attach it manually in WhatsApp Web themselves.
   * Probed once with a dummy PDF File.
   */
  readonly canShareFiles: boolean = (() => {
    try {
      const nav = window.navigator as Navigator & {
        canShare?: (data: { files: File[] }) => boolean;
        share?: (data: ShareData & { files?: File[] }) => Promise<void>;
      };
      if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') {
        return false;
      }
      const probe = new File([new Uint8Array([0x25, 0x50, 0x44, 0x46])], 'probe.pdf', {
        type: 'application/pdf',
      });
      return nav.canShare({ files: [probe] });
    } catch {
      return false;
    }
  })();

  private destroy$ = new Subject<void>();

  constructor(
    private userService: UsersService,
    private hebcalService: HebcalService,
    private store: Store<AppStore>,
  ) {}

  ngOnInit(): void {
    this.fetchUsers();
    this.getHebrewDate();

    // Keep the split-in-half lists in sync with the store.
    this.usersList$.pipe(takeUntil(this.destroy$)).subscribe((users) => {
      const mid = Math.ceil(users.length / 2);
      this.usersPart1 = users.slice(0, mid);
      this.usersPart2 = users.slice(mid);
    });
  }

  private fetchUsers(): void {
    this.userService
      .getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fetched) =>
          fetched.forEach((user) => this.store.dispatch(addUserToStore({ user }))),
        error: (err) => console.error('Error fetching users:', err),
      });
  }

  private getHebrewDate(): void {
    this.hebcalService
      .getHebrewDate()
      .then((data) => (this.shabbatData = data))
      .catch((error) => console.error('Error fetching Shabbat data:', error));
  }

  // ── Actions ─────────────────────────────────────────────────────────

  onPrint(): void {
    window.print();
  }

  /** Generate the balance sheet as a PDF File and download it. */
  async downloadPdf(): Promise<void> {
    if (this.pdfBusy) return;
    this.pdfBusy = true;
    try {
      const blob = await this.buildPdfBlob();
      this.triggerDownload(blob, this.makeFileName());
    } catch (err) {
      console.error('Download balance PDF failed:', err);
      alert('שגיאה ביצירת הקובץ');
    } finally {
      this.pdfBusy = false;
    }
  }

  /**
   * Generate the PDF and share it as a File via the native Web Share
   * API. Button is only rendered when `canShareFiles` is true, so this
   * path is guaranteed to work — no desktop fallback that asks the user
   * to manually attach.
   */
  async shareViaWhatsApp(): Promise<void> {
    if (this.pdfBusy || !this.canShareFiles) return;
    this.pdfBusy = true;
    try {
      const blob = await this.buildPdfBlob();
      const filename = this.makeFileName();
      const file = new File([blob], filename, { type: 'application/pdf' });

      const nav = window.navigator as Navigator & {
        share: (data: ShareData & { files?: File[] }) => Promise<void>;
      };

      try {
        await nav.share({
          title: this.shareTitle(),
          text: this.shareCaption(),
          files: [file],
        });
      } catch (err) {
        if ((err as { name?: string })?.name !== 'AbortError') {
          console.error('navigator.share failed:', err);
          alert('שיתוף הקובץ נכשל');
        }
      }
    } catch (err) {
      console.error('Share balance PDF failed:', err);
      alert('שגיאה ביצירת הקובץ לשיתוף');
    } finally {
      this.pdfBusy = false;
    }
  }

  // ── PDF helpers ────────────────────────────────────────────────────

  /**
   * Capture the balance table (`.tableWrap`) as an A4 portrait PDF.
   * Uses html2canvas-pro (handles Tailwind/daisyUI's oklch() colors);
   * skips any `.noPrint` element so the toolbar isn't in the capture.
   */
  private async buildPdfBlob(): Promise<Blob> {
    const element = document.querySelector('.tableWrap') as HTMLElement | null;
    if (!element) throw new Error('.tableWrap element not found');

    // Same pattern as print-aliyot — apply pdf-export-mode so any
    // print-oriented tweaks are active during capture.
    document.body.classList.add('pdf-export-mode');
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);

    // Lazy-load the heavy PDF stack only when the user actually asks for it.
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas-pro'),
      import('jspdf'),
    ]);

    try {
      const canvas = await html2canvas(element, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        ignoreElements: (n: Element) =>
          n instanceof HTMLElement && n.classList?.contains('noPrint'),
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.97);

      // A4 portrait: 210 × 297 mm, 6mm safe margin.
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pageW = 210;
      const pageH = 297;
      const margin = 6;
      const availW = pageW - 2 * margin;
      const availH = pageH - 2 * margin;

      const ratio = canvas.height / canvas.width;
      let renderW = availW;
      let renderH = renderW * ratio;
      if (renderH > availH) {
        renderH = availH;
        renderW = renderH / ratio;
      }
      // Horizontal: center. Vertical: TOP-align (was centered, which
      // left a big empty strip above wide-and-short content like the
      // balance sheet). Any spare space now falls at the bottom.
      const offsetX = (pageW - renderW) / 2;
      const offsetY = margin;

      pdf.addImage(imgData, 'JPEG', offsetX, offsetY, renderW, renderH);

      return pdf.output('blob');
    } finally {
      document.body.classList.remove('pdf-export-mode');
    }
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  private makeFileName(): string {
    const date = this.pdfDatePrefix(this.shabbatData?.shabbatStart);
    return `${date}_balance.pdf`;
  }

  /** YYYY-MM-DD prefix used in exported filenames. Sortable + unambiguous. */
  private pdfDatePrefix(iso?: string): string {
    let d: Date;
    try {
      d = iso ? new Date(iso) : new Date();
      if (Number.isNaN(d.getTime())) d = new Date();
    } catch {
      d = new Date();
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private shareTitle(): string {
    const parasha = this.shabbatData?.parasha ?? '';
    return parasha ? `יתרות מתפללים - שבת ${parasha}` : 'יתרות מתפללים';
  }

  private shareCaption(): string {
    const parasha = this.shabbatData?.parasha || '';
    const lines = [
      `💰 יתרות מתפללים`,
      parasha ? `שבת פרשת ${parasha}` : '',
      this.shabbatData?.hebrew || '',
      '',
      `שבת שלום ומבורך 🙏`,
      `בית הכנסת תפארת ישראל, גני צבי`,
    ];
    return lines.join('\n');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
